import { useCallback, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { useDelete } from '@campai/lib-web/components'
import {
    Button,
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
} from '@campai/lib-web/components/new/ui'
import { client } from '@campai/lib-web/utils/trpc'

import { appState } from '@/appState'

export const DangerZone = observer(() => {
    const { t } = useTranslation()
    const { submitDelete, isDeleting } = useDelete()
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [confirmationValue, setConfirmationValue] = useState('')

    const i18nKey = 'profile.dangerZone'

    const handleDelete = useCallback(async () => {
        if (confirmationValue !== appState.user.email) {
            return
        }

        const deleted = await submitDelete(async () => {
            await client.currentUser.deleteUser.mutate()
            await appState.auth.logout()
        })

        if (deleted) {
            setDeleteDialogOpen(false)
        }
    }, [confirmationValue, submitDelete])

    return (
        <Card>
            <CardHeader className="border-b">
                <CardTitle>{t(`${i18nKey}.title`)}</CardTitle>
                <CardDescription>{t(`${i18nKey}.description`)}</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="flex flex-col gap-4 md:flex-row md:items-center">
                    <div className="flex-1">
                        <div className="mb-1 font-medium">{t(`${i18nKey}.delete.title`)}</div>
                        <div className="whitespace-pre-wrap text-sm">{t(`${i18nKey}.delete.description`)}</div>
                    </div>
                    <Button
                        variant="destructive"
                        className="w-full md:w-auto"
                        onClick={() => {
                            setConfirmationValue('')
                            setDeleteDialogOpen(true)
                        }}
                    >
                        {t(`${i18nKey}.delete.action`)}
                    </Button>
                </div>
            </CardContent>
            <Dialog
                open={deleteDialogOpen}
                onOpenChange={(open) => {
                    if (!isDeleting) {
                        setDeleteDialogOpen(open)
                    }
                }}
            >
                <DialogContent showCloseButton={false}>
                    <DialogHeader>
                        <DialogTitle>{t(`${i18nKey}.delete.confirmation.title`)}</DialogTitle>
                        <DialogDescription>{t(`${i18nKey}.delete.confirmation.message`)}</DialogDescription>
                    </DialogHeader>
                    <div>
                        <div className="border-border mb-2 border-t pt-6">
                            <span>{t('components.dialog.confirm.prefix')}</span>
                            <div className="bg-muted my-3 rounded-md p-2 font-mono text-sm tracking-wide">
                                {appState.user.email}
                            </div>
                        </div>
                        <Input
                            autoFocus
                            value={confirmationValue}
                            placeholder={appState.user.email}
                            aria-label={t('components.dialog.confirm.prefix')}
                            disabled={isDeleting}
                            onChange={(event) => setConfirmationValue(event.currentTarget.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && confirmationValue === appState.user.email) {
                                    event.preventDefault()
                                    void handleDelete()
                                }
                            }}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            disabled={isDeleting}
                            onClick={() => setDeleteDialogOpen(false)}
                        >
                            {t('actions.cancel')}
                        </Button>
                        <Button
                            type="button"
                            variant="destructive"
                            loading={isDeleting}
                            disabled={isDeleting || confirmationValue !== appState.user.email}
                            onClick={() => void handleDelete()}
                        >
                            {t('actions.delete')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    )
})
